import { useLayoutEffect, type RefObject } from 'react'
import ar from '../data/bombTimerLegalTranslations/ar.json'
import de from '../data/bombTimerLegalTranslations/de.json'
import en from '../data/bombTimerLegalTranslations/en.json'
import es from '../data/bombTimerLegalTranslations/es.json'
import fr from '../data/bombTimerLegalTranslations/fr.json'
import hi from '../data/bombTimerLegalTranslations/hi.json'
import id from '../data/bombTimerLegalTranslations/id.json'
import ja from '../data/bombTimerLegalTranslations/ja.json'
import ko from '../data/bombTimerLegalTranslations/ko.json'
import ptBR from '../data/bombTimerLegalTranslations/pt-BR.json'
import zhHans from '../data/bombTimerLegalTranslations/zh-Hans.json'
import zhHant from '../data/bombTimerLegalTranslations/zh-Hant.json'

const translations = {
  ar,
  de,
  en,
  es,
  fr,
  hi,
  id,
  ja,
  ko,
  'pt-BR': ptBR,
  'zh-Hans': zhHans,
  'zh-Hant': zhHant,
}

export const bombTimerLegalLocales = [
  { code: 'en', label: 'English', htmlLang: 'en' },
  { code: 'ar', label: 'العربية', htmlLang: 'ar' },
  { code: 'zh-Hans', label: '简体中文', htmlLang: 'zh-Hans' },
  { code: 'zh-Hant', label: '繁體中文', htmlLang: 'zh-Hant' },
  { code: 'fr', label: 'Français', htmlLang: 'fr' },
  { code: 'de', label: 'Deutsch', htmlLang: 'de' },
  { code: 'hi', label: 'हिन्दी', htmlLang: 'hi' },
  { code: 'id', label: 'Bahasa Indonesia', htmlLang: 'id' },
  { code: 'ja', label: '日本語', htmlLang: 'ja' },
  { code: 'ko', label: '한국어', htmlLang: 'ko' },
  { code: 'pt-BR', label: 'Português (Brasil)', htmlLang: 'pt-BR' },
  { code: 'es', label: 'Español', htmlLang: 'es' },
] as const

export type BombTimerLegalLocale = (typeof bombTimerLegalLocales)[number]['code']
export type BombTimerLegalDocument = 'privacy-policy' | 'terms-and-conditions'

const supportedLocales = new Set<string>(bombTimerLegalLocales.map(({ code }) => code))

export function getBombTimerLegalLocale(value?: string): BombTimerLegalLocale {
  return value && supportedLocales.has(value) ? (value as BombTimerLegalLocale) : 'en'
}

export function getBombTimerLegalPath(
  document: BombTimerLegalDocument,
  locale: BombTimerLegalLocale,
) {
  const basePath = `/apps/bomb-timer/${document}`
  return locale === 'en' ? basePath : `${basePath}/${locale}`
}

export function getBombTimerLegalTranslation(locale: BombTimerLegalLocale, source: string) {
  const localeTranslations = translations[locale] as Record<string, string>
  return localeTranslations[source] ?? source
}

export function useBombTimerLegalTranslation(
  rootRef: RefObject<HTMLElement | null>,
  locale: BombTimerLegalLocale,
) {
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root || locale === 'en') return

    const translate = (source: string) => getBombTimerLegalTranslation(locale, source)
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let node = walker.nextNode()

    while (node) {
      const source = node.textContent?.replace(/\s+/g, ' ').trim()
      if (source) {
        const translated = translate(source)
        if (translated !== source) {
          const leadingWhitespace = node.textContent?.match(/^\s*/)?.[0] ?? ''
          const trailingWhitespace = node.textContent?.match(/\s*$/)?.[0] ?? ''
          node.textContent = `${leadingWhitespace}${translated}${trailingWhitespace}`
        }
      }
      node = walker.nextNode()
    }
  }, [locale, rootRef])
}
